import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Sparkles, Eye, Star, Clock, Undo2, Search } from 'lucide-react';
import { KitxensMark } from './Logo';
import { useT } from '../i18n';

/* HeroShowcase — light-themed, living product slideshow for the hero.
   Slides remount on rotation, so staggered entrances replay = feels alive. */

const INTERVAL = 8000;
const FIRST_INTERVAL = 12000; // extra reading time on the very first slide

/* On small screens skip staggered entrances: guarantees content is always
   fully visible (some mobile browsers throttle animation frames). */
const isSmallScreen = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

const rise = (i) =>
  isSmallScreen
    ? {}
    : {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4, delay: 0.15 + i * 0.09 },
      };

function Chip({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11]/10 border border-[#F77F11]/40 px-3 py-1 text-[10px] font-bold tracking-[0.16em] uppercase text-[#FFB672]">
      {children}
    </span>
  );
}

function LiveDot() {
  return <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />;
}

/* Ticking counter — increments periodically so the panel feels fed with data. */
function TickingNumber({ base, step = 1, every = 2200, format = (n) => n.toLocaleString() }) {
  const [n, setN] = useState(base);
  useEffect(() => {
    const t = setInterval(() => setN((v) => v + Math.ceil(Math.random() * step)), every);
    return () => clearInterval(t);
  }, [step, every]);
  return <>{format(n)}</>;
}

/* Cycles through values with a soft fade — data that visibly changes. */
function Cycler({ items, every = 3000, className = '' }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % items.length), every);
    return () => clearInterval(t);
  }, [items.length, every]);
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={i}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.3 }}
        className={className}
      >
        {items[i]}
      </motion.span>
    </AnimatePresence>
  );
}

function SlideIndexAi({ isEs }) {
  return (
    <div className="h-full flex flex-col p-6 sm:p-7">
      <motion.div {...rise(0)}><Chip>{isEs ? 'Búsqueda IA · En Vivo' : 'AI Search · Live'}</Chip></motion.div>
      <motion.div {...rise(1)} className="mt-4 rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3 flex items-center gap-3">
        <Search className="w-4 h-4 text-neutral-400 shrink-0" />
        <Cycler
          className="text-[13.5px] text-neutral-200 min-w-0 truncate"
          every={3200}
          items={[
            'Best brunch near Mountain View?',
            isEs ? '¿Cena romántica esta noche?' : 'Romantic dinner tonight?',
            isEs ? '¿Catering para mi oficina?' : 'Catering for my office?',
          ]}
        />
        <Cycler
          className="ml-auto text-[10px] font-bold tracking-[0.16em] text-neutral-400 shrink-0"
          every={3200}
          items={['CHATGPT', 'GEMINI', 'PERPLEXITY']}
        />
      </motion.div>
      <motion.div {...rise(2)} className="mt-3 rounded-2xl bg-white/[0.06] border border-white/10 p-4 sm:p-5 flex flex-col">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-[11.5px] font-semibold text-neutral-300">
            <span className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </span>
            {isEs ? 'Respuesta de la IA' : 'AI answer'}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400"><LiveDot /> Live</span>
        </div>
        <p className="mt-3.5 text-[14px] sm:text-[15px] leading-relaxed text-neutral-200">
          {isEs ? 'Una gran opción cerca de Mountain View es ' : 'A great option near Mountain View is '}
          <span className="inline-flex items-center rounded-lg border border-[#F77F11]/50 bg-[#F77F11]/10 px-2 py-0.5 font-semibold text-[#FFB672]">
            {isEs ? 'Tu Restaurante' : 'Your Restaurant'}
          </span>
          {isEs
            ? ' — brunch artesanal, terraza y una valoración media de 4.8.'
            : ' — artisan brunch, open terrace and a 4.8 average rating.'}
        </p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {['ChatGPT', 'Gemini', 'Perplexity', 'Google AI'].map((e, i) => (
            <motion.div key={e} {...rise(3 + i)} className="rounded-xl bg-white/[0.05] border border-white/10 px-2 py-2 text-center">
              <p className="text-[10px] font-semibold text-neutral-300 truncate">{e}</p>
              <p className="mt-0.5 text-[10px] font-bold text-emerald-400">✓ {isEs ? 'Visible' : 'Listed'}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {['★ 4.8 · 1,204 ' + (isEs ? 'reseñas' : 'reviews'), isEs ? 'Reservas OpenTable' : 'OpenTable reservations', isEs ? 'Abierto ahora' : 'Open now'].map((c, i) => (
            <motion.span key={c} {...rise(3 + i)} className="rounded-full bg-white/[0.05] border border-white/10 px-3 py-1 text-[10.5px] text-neutral-300">
              {c}
            </motion.span>
          ))}
        </div>
      </motion.div>
      <motion.div {...rise(5)} className="mt-3 flex-1 rounded-2xl bg-white/[0.06] border border-white/10 p-4 sm:p-5 hidden sm:flex flex-col justify-center gap-2.5">
        <p className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-neutral-400">
          {isEs ? 'Más preguntas que ganas' : 'More questions you win'}
        </p>
        {[
          isEs ? '“¿Catering para 40 personas en San José?”' : '“Catering for 40 people in San José?”',
          isEs ? '“¿Mejores tacos abiertos ahora?”' : '“Best tacos open right now?”',
        ].map((q, i) => (
          <motion.div key={q} {...rise(6 + i)} className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2">
            <p className="text-[11.5px] text-neutral-300 italic min-w-0 truncate">{q}</p>
            <span className="shrink-0 text-[10.5px] font-bold text-[#FFB672] bg-[#F77F11]/10 border border-[#F77F11]/40 rounded-full px-2.5 py-0.5">
              {isEs ? 'Tu Restaurante' : 'Your Restaurant'}
            </span>
          </motion.div>
        ))}
      </motion.div>
      <motion.div {...rise(6)} className="mt-3 flex items-center justify-between gap-3 text-[10.5px]">
        <p className="text-neutral-400 min-w-0 truncate">{isEs ? 'Fuentes' : 'Sources'}: Schema.org · Google · Yelp · OpenTable</p>
        <p className="font-semibold text-[#F77F11] shrink-0">{isEs ? 'Indexado por Kitxens' : 'Indexed by Kitxens'}</p>
      </motion.div>
    </div>
  );
}

const PLATFORMS = [
  { l: 'Toast POS', c: '#FF4C00', i: 'T' },
  { l: 'Square', c: '#1a1a1a', i: 'S' },
  { l: 'Google Business', c: '#4285F4', i: 'G' },
  { l: 'Delivery Platforms', c: '#FF3008', i: 'D' },
  { l: 'Reviews', c: '#F5B300', i: '★' },
];

function SlideIntelligence({ isEs }) {
  const outcomes = [
    { icon: TrendingUp, l: isEs ? 'Ingresos' : 'Revenue', vals: ['+18%', '+19%'] },
    { icon: Sparkles, l: isEs ? 'Pedidos' : 'Orders', vals: ['+24%', '+26%'] },
    { icon: Eye, l: isEs ? 'Visibilidad IA' : 'AI Visibility', vals: ['+32%', '+34%'] },
    { icon: Star, l: isEs ? 'Reseñas' : 'Reviews', vals: ['+45%', '+47%'] },
  ];
  return (
    <div className="h-full flex flex-col p-6 sm:p-7">
      <motion.div {...rise(0)}><Chip>{isEs ? 'Centro de Mando · En Vivo' : 'Command Center · Live'}</Chip></motion.div>
      <motion.p {...rise(1)} className="mt-3 text-[13px] text-neutral-500">
        {isEs ? 'Insights en tiempo real. Operación más inteligente. Más ingresos.' : 'Real-time insights. Smarter operations. More revenue.'}
      </motion.p>
      <div className="mt-4 flex-1 grid grid-cols-2 sm:grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-4">
        <div className="space-y-2">
          <p className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-neutral-400">{isEs ? 'Conectado' : 'Connected'}</p>
          {PLATFORMS.map((p, i) => (
            <motion.div key={p.l} {...rise(1 + i)} className={`${i >= 4 ? 'hidden sm:flex' : 'flex'} items-center gap-2 rounded-xl bg-white/[0.06] border border-white/10 px-2.5 py-2`}>
              <span className="w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ backgroundColor: p.c }}>{p.i}</span>
              <span className="text-[11px] text-neutral-200 min-w-0 truncate">{p.l}</span>
              <span className="ml-auto shrink-0"><LiveDot /></span>
            </motion.div>
          ))}
        </div>
        <motion.div {...rise(2)} className="hidden sm:flex flex-col items-center gap-2">
          <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center"
            style={{ background: 'radial-gradient(circle, rgba(247,127,17,0.22), transparent 70%)' }}>
            <motion.div
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden ring-2 ring-[#F77F11]/50 shadow-[0_15px_40px_-10px_rgba(247,127,17,0.6)]"
            >
              <KitxensMark className="w-full h-full object-cover" alt="Kitxens" />
            </motion.div>
          </div>
          <p className="text-[9px] font-bold tracking-[0.25em] text-neutral-500">KITXENS</p>
        </motion.div>
        <div className="space-y-2">
          <p className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-neutral-400 text-right">{isEs ? 'Resultados' : 'Outcomes'}</p>
          {outcomes.map((o, i) => (
            <motion.div key={o.l} {...rise(2 + i)} className="flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/10 px-2.5 py-2">
              <o.icon className="w-3.5 h-3.5 text-[#F77F11] shrink-0" />
              <span className="text-[11px] text-neutral-200 min-w-0 truncate">{o.l}</span>
              <Cycler className="ml-auto text-[11px] font-bold text-emerald-400 shrink-0" every={2600 + i * 700} items={o.vals} />
            </motion.div>
          ))}
        </div>
      </div>
      <motion.div {...rise(7)} className="mt-4 flex items-center gap-2.5 rounded-xl bg-emerald-400/10 border border-emerald-400/20 px-3 py-2">
        <LiveDot />
        <span className="text-[10.5px] font-bold text-emerald-400 shrink-0">LIVE</span>
        <span className="text-[10.5px] text-neutral-500 min-w-0 truncate">
          <TickingNumber base={12408} step={3} /> {isEs ? 'señales procesadas esta semana' : 'restaurant signals this week'}
        </span>
        <svg viewBox="0 0 120 20" className="ml-auto w-24 h-4 shrink-0" preserveAspectRatio="none">
          <polyline points="0,12 12,10 24,13 36,8 48,11 60,7 72,10 84,6 96,9 108,5 120,8" fill="none" stroke="#10b981" strokeWidth="1.5" strokeDasharray="200" strokeDashoffset="200">
            <animate attributeName="stroke-dashoffset" from="200" to="0" dur="2.5s" fill="freeze" />
          </polyline>
        </svg>
      </motion.div>
    </div>
  );
}

function SlideOperations({ isEs }) {
  return (
    <div className="h-full flex flex-col p-6 sm:p-7">
      <motion.div {...rise(0)}><Chip>{isEs ? 'Operación Inteligente · 24/7' : 'Smart Operations · 24/7'}</Chip></motion.div>
      <div className="mt-4 flex-1 grid grid-cols-2 gap-3">
        <motion.div {...rise(1)} className="rounded-2xl bg-white/[0.06] border border-white/10 p-4 flex flex-col">
          <p className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-neutral-400">{isEs ? 'Costeo de recetas' : 'Recipe costing'}</p>
          <p className="mt-2 text-[14px] font-semibold text-white">Chicken Burrito</p>
          <div className="mt-auto space-y-1.5 text-[11.5px]">
            <div className="flex justify-between text-neutral-500"><span>{isEs ? 'Costo' : 'Food cost'}</span><span className="text-neutral-100 font-medium">$3.61</span></div>
            <div className="flex justify-between text-neutral-500"><span>{isEs ? 'Margen' : 'Margin'}</span><span className="font-bold text-emerald-400">72%</span></div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: '28%' }} transition={{ duration: 1, delay: 0.5 }} className="h-full rounded-full bg-[#F77F11]" />
            </div>
          </div>
        </motion.div>
        <motion.div {...rise(2)} className="rounded-2xl bg-white/[0.06] border border-white/10 p-4 flex flex-col">
          <p className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-neutral-400">{isEs ? 'Mix de canales' : 'Channel mix'}</p>
          <p className="mt-2 text-[11.5px] text-neutral-500 leading-snug">{isEs ? 'Clientes fieles → canal directo, 0% comisión' : 'Loyal guests → direct channel, 0% commission'}</p>
          <div className="mt-auto">
            <p className="text-2xl font-bold text-emerald-400">$<TickingNumber base={1240} step={2} every={2600} /><span className="text-[11px] font-medium text-neutral-500">/{isEs ? 'mes' : 'mo'}</span></p>
            <p className="text-[10.5px] text-neutral-500">{isEs ? 'ahorrado en comisiones' : 'saved in commissions'}</p>
          </div>
        </motion.div>
        <div className="col-span-2 grid grid-cols-3 gap-3">
          {[
            { icon: Clock, l: isEs ? 'Tiempo medio' : 'Avg. time', vals: ['24 min', '23 min'], c: 'text-white' },
            { icon: Undo2, l: isEs ? 'Reembolsos' : 'Refunds', vals: ['-18%', '-19%'], c: 'text-emerald-400' },
            { icon: Star, l: isEs ? 'Rating delivery' : 'Delivery rating', vals: ['4.8★'], c: 'text-[#FFB672]' },
          ].map((m, i) => (
            <motion.div key={m.l} {...rise(3 + i)} className="rounded-2xl bg-white/[0.06] border border-white/10 p-3.5">
              <m.icon className="w-3.5 h-3.5 text-[#F77F11]" />
              <p className={`mt-2 text-[17px] font-bold ${m.c}`}>
                <Cycler items={m.vals} every={3400 + i * 800} />
              </p>
              <p className="text-[10px] text-neutral-500">{m.l}</p>
            </motion.div>
          ))}
        </div>
        <motion.div {...rise(6)} className="col-span-2 rounded-2xl bg-emerald-400/10 border border-emerald-400/20 p-4">
          <div className="flex items-center gap-2">
            <LiveDot />
            <p className="text-[9.5px] font-bold tracking-[0.16em] uppercase text-emerald-300">{isEs ? 'Tu equipo IA este mes' : 'Your AI team this month'}</p>
          </div>
          <div className="mt-2.5 grid grid-cols-3 gap-2 text-center">
            <div><p className="text-[16px] font-bold text-white"><TickingNumber base={142} step={1} every={3000} /></p><p className="text-[9.5px] text-neutral-500">{isEs ? 'llamadas atendidas' : 'calls answered'}</p></div>
            <div><p className="text-[16px] font-bold text-white"><TickingNumber base={96} step={1} every={4200} /></p><p className="text-[9.5px] text-neutral-500">{isEs ? 'reseñas respondidas' : 'reviews replied'}</p></div>
            <div><p className="text-[16px] font-bold text-emerald-400">31h</p><p className="text-[9.5px] text-neutral-500">{isEs ? 'ahorradas' : 'saved'}</p></div>
          </div>
        </motion.div>
      </div>
      <motion.p {...rise(6)} className="mt-3 text-[10.5px] text-neutral-400">
        {isEs ? 'Menú, zonas y empaque revisados cada mes por KITXENS.' : 'Menu, zones and packaging reviewed monthly by KITXENS.'}
      </motion.p>
    </div>
  );
}

const SLIDES = [SlideIndexAi, SlideIntelligence, SlideOperations];

export default function HeroShowcase() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [idx, setIdx] = useState(0);
  const firstCycle = useRef(true);

  useEffect(() => {
    const delay = firstCycle.current ? FIRST_INTERVAL : INTERVAL;
    const t = setTimeout(() => {
      firstCycle.current = false;
      setIdx((i) => (i + 1) % SLIDES.length);
    }, delay);
    return () => clearTimeout(t);
  }, [idx]);

  const Active = SLIDES[idx];

  return (
    <div className="relative" data-testid="hero-showcase">
      {/* warm halo for contrast against the white page */}
      <div className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-gradient-to-br from-[#F77F11]/30 via-amber-300/15 to-orange-400/25 blur-3xl opacity-90" />

      <div
        className="relative rounded-3xl overflow-hidden h-[540px] sm:h-[580px] ring-1 ring-white/[0.12]"
        style={{
          background: 'linear-gradient(140deg, #2b3242 0%, #1e2432 45%, #2a2233 100%)',
          boxShadow:
            '0 60px 130px -45px rgba(15,17,23,0.6), 0 30px 70px -35px rgba(247,127,17,0.35), inset 0 1px 0 rgba(255,255,255,0.1)',
        }}
      >
        {/* top light streak */}
        <div className="pointer-events-none absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        {/* corner glows */}
        <div className="pointer-events-none absolute -top-28 -right-20 w-80 h-80 rounded-full bg-[#F77F11]/[0.14] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-[#5B8CFF]/[0.08] blur-3xl" />
        {/* subtle dot grid texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0"
          >
            <Active isEs={isEs} />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            data-testid={`showcase-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-7 bg-[#F77F11]' : 'w-2.5 bg-neutral-300 hover:bg-neutral-400'}`}
          />
        ))}
      </div>
    </div>
  );
}
